/**
 * @file useFormModal.ts
 * @description Hook for managing modal state with form data
 * @module hooks
 */

import { useState, useCallback } from 'react';
import { useDisclosure } from '@mantine/hooks';

interface UseFormModalOptions<T> {
  /** Initial form data */
  initialData?: Partial<T>;
}

interface UseFormModalReturn<T, S = null> {
  /** Whether modal is open */
  isOpen: boolean;
  /** Open the modal */
  open: () => void;
  /** Close the modal and reset state */
  close: () => void;
  /** Form data */
  formData: Partial<T>;
  /** Update form data */
  setFormData: React.Dispatch<React.SetStateAction<Partial<T>>>;
  /** Selected item (for edit modals) */
  selectedItem: S | null;
  /** Open modal for editing with pre-populated data */
  openForEdit: (item: S, data: Partial<T>) => void;
}

/**
 * Hook for managing modal state with form data
 * Combines useDisclosure with form state management
 */
export function useFormModal<T extends object, S = null>(
  options: UseFormModalOptions<T> = {}
): UseFormModalReturn<T, S> {
  const { initialData = {} } = options;
  const [opened, { open, close: closeModal }] = useDisclosure(false);
  const [formData, setFormData] = useState<Partial<T>>(initialData);
  const [selectedItem, setSelectedItem] = useState<S | null>(null);

  const close = useCallback(() => {
    closeModal();
    setFormData(initialData);
    setSelectedItem(null);
  }, [closeModal, initialData]);

  const openForEdit = useCallback(
    (item: S, data: Partial<T>) => {
      setSelectedItem(item);
      setFormData(data);
      open();
    },
    [open]
  );

  return {
    isOpen: opened,
    open,
    close,
    formData,
    setFormData,
    selectedItem,
    openForEdit,
  };
}
